import React, { RefObject } from "react";
import { Plan, PlanDetailsPageSection } from "../../../@types";
import { useTranslate, planDataUtils } from "../../../helpers";
import { shouldSuppressPlanBenefits } from "../../../helpers/planHelpers";
import { hasOpioidTreatmentBenefit } from "../../../helpers/benefitHelpers";
import { PlanDetailSection } from "../components";
import { useSubSectionCaption } from "../components/helper";
import { ToggleMoreBenefits } from "../components/ToggleMoreBenefits";
import { convertBenefitData } from "../components/benefitHelper";
import {
  getAdditionalBenefitsForMMP,
  getComprehensiveDentalBenefits,
  getHearingBenefits,
  getMoreBenefits,
  getNonOpioidBenefits,
  getPreventiveDentalBenefits,
  getSpecialBenefits,
  getVisionBenefits,
} from "./data";
import {
  convertMMPBenefitData,
  convertMoreBenefitData,
  convertSpecialBenefitData,
} from "./helper";
import { SubSectionTable } from "./helper";

const ExtraBenefitsSection: React.FunctionComponent<{
  plan: Plan;
  sectionRef: RefObject<HTMLDivElement>;
}> = ({ plan, sectionRef }) => {
  const t = useTranslate();
  const suppressed = shouldSuppressPlanBenefits(plan);

  const hasMoreBenefits =
    plan.additional_supplemental_benefits &&
    !!plan.additional_supplemental_benefits.other_benefits.length;
  const onlyMoreBenefitIsOpioidTreatment =
    plan.additional_supplemental_benefits &&
    plan.additional_supplemental_benefits.other_benefits.length === 1 &&
    hasOpioidTreatmentBenefit(plan);

  const specialBenefitsCaption = useSubSectionCaption(
    "special_benefit.special_benefits",
    "special_benefit.heading_description"
  );

  return (
    <PlanDetailSection
      id={PlanDetailsPageSection.EXTRA_BENEFITS}
      title={t("plan_terms.extra_benefits_and_costs")}
      sectionRef={sectionRef}
    >
      <SubSectionTable
        key="HearingBenefits"
        data={convertBenefitData(
          plan,
          suppressed,
          t,
          "hearing",
          getHearingBenefits(plan, t),
          t("plan_terms.hearing")
        )}
        caption={t("plan_terms.hearing")}
        hasLimits={true}
      />
      <SubSectionTable
        key="PreventiveDentalBenefits"
        data={convertBenefitData(
          plan,
          suppressed,
          t,
          "preventive_dental",
          getPreventiveDentalBenefits(t),
          t("plan_terms.preventive_dental.short")
        )}
        caption={useSubSectionCaption(
          "plan_terms.preventive_dental.short",
          "plan_terms.preventive_dental.definition"
        )}
        hasLimits={true}
      />
      <SubSectionTable
        key="ComprehensiveDentalBenefits"
        data={convertBenefitData(
          plan,
          suppressed,
          t,
          "comprehensive_dental",
          getComprehensiveDentalBenefits(t),
          t("plan_terms.comprehensive_dental.short")
        )}
        caption={useSubSectionCaption(
          "plan_terms.comprehensive_dental.short",
          "plan_terms.comprehensive_dental.definition"
        )}
        hasLimits={true}
      />
      <SubSectionTable
        key="VisionBenefits"
        data={convertBenefitData(
          plan,
          suppressed,
          t,
          "vision",
          getVisionBenefits(t),
          t("plan_terms.vision")
        )}
        caption={t("plan_terms.vision")}
        hasLimits={true}
      />
      <ToggleMoreBenefits
        buttonTextToOpen={t("plan_terms.extra_benefits.open")}
        buttonTextToCollapse={t("plan_terms.extra_benefits.collapse")}
        section="extra-benefits"
      >
        <SubSectionTable
          key="NonOpioidBenefits"
          data={convertMoreBenefitData(
            plan,
            suppressed,
            t,
            getNonOpioidBenefits(t)
          )}
          caption={t("plan_terms.non_opioid_pain_management")}
          hasLimits={false}
        />

        {hasMoreBenefits && !onlyMoreBenefitIsOpioidTreatment && (
          <SubSectionTable
            key="MoreBenefits"
            data={convertMoreBenefitData(
              plan,
              suppressed,
              t,
              getMoreBenefits(t)
            )}
            caption={t("more_benefits.title")}
            hasLimits={false}
          />
        )}

        {plan.additional_supplemental_benefits &&
          plan.additional_supplemental_benefits.special_benefits.length > 0 && (
            <SubSectionTable
              key="SpecialBenefits"
              data={convertSpecialBenefitData(
                plan,
                suppressed,
                t,
                getSpecialBenefits(t)
              )}
              caption={specialBenefitsCaption}
              hasLimits={false}
            />
          )}

        {planDataUtils.showMMPBenefits(plan) && (
          <SubSectionTable
            key="MMPBenefits"
            data={convertMMPBenefitData(
              plan,
              suppressed,
              t,
              getAdditionalBenefitsForMMP(t)
            )}
            caption={t("plan_terms.mmp_additional_benefits_and_services")}
            hasLimits={false}
          />
        )}
      </ToggleMoreBenefits>
    </PlanDetailSection>
  );
};
export { ExtraBenefitsSection };
